import React, { useEffect, useMemo, useState } from 'react';
import { useUtmTaxonomyStore, type Impact } from '@/store/utm-taxonomy-store';
import { ChevronDown, ChevronUp, X } from 'lucide-react';
import { calculateArea } from '@/utils/utm-geometry';

type ResultsTab = 'my_map' | 'intersections' | 'constraints';

const IMPACT_LEVEL_ORDER: Impact['level'][] = [
  'NOTAM',
  'AUTHORISATION',
  'NOTIFICATION',
  'NOTHING_TO_DO',
];

const IMPACT_LEVEL_META: Record<Impact['level'], { label: string; dotClass: string }> = {
  NOTAM: { label: 'NOTAMS', dotClass: 'bg-destructive' },
  AUTHORISATION: { label: 'Authorizations', dotClass: 'bg-destructive' },
  NOTIFICATION: { label: 'Notifications', dotClass: 'bg-primary' },
  NOTHING_TO_DO: { label: 'Information', dotClass: 'bg-muted-foreground' },
};

const UtmSelectionResultsPanel: React.FC = () => {
  const {
    selectionResults,
    clearSelection,
    selectZone,
    selectionFeatures,
    activeSelectionFeatureId,
    drawingElevation,
    visibleIntersectionZoneIdsByFeatureId,
    setIntersectionZoneVisible,
    setHoveredZoneIds,
    ensureZoneGeometries,
  } = useUtmTaxonomyStore();

  const [activeTab, setActiveTab] = useState<ResultsTab>('my_map');
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 1024);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const activeGeometry = useMemo(() => {
    if (!activeSelectionFeatureId) return null;
    const feature = selectionFeatures.find((entry) => entry.id === activeSelectionFeatureId);
    return feature?.geometry ?? null;
  }, [selectionFeatures, activeSelectionFeatureId]);

  const selectedAreaKm2 = useMemo(() => {
    if (!activeGeometry) return 0;
    const areaM2 = calculateArea(activeGeometry);
    return areaM2 / 1_000_000;
  }, [activeGeometry]);

  const visibilityState = useMemo(() => {
    if (!activeSelectionFeatureId) return null;
    const ids = visibleIntersectionZoneIdsByFeatureId[activeSelectionFeatureId];
    if (!ids) return null;
    return new Set(ids);
  }, [activeSelectionFeatureId, visibleIntersectionZoneIdsByFeatureId]);

  const impactsByLevel = useMemo(() => {
    const grouped: Record<Impact['level'], Impact[]> = {
      NOTAM: [],
      AUTHORISATION: [],
      NOTIFICATION: [],
      NOTHING_TO_DO: [],
    };

    (selectionResults?.impacts ?? []).forEach((impact) => {
      grouped[impact.level]?.push(impact);
    });

    return grouped;
  }, [selectionResults]);

  useEffect(() => {
    setHoveredZoneIds([]);
    return () => setHoveredZoneIds([]);
  }, [activeSelectionFeatureId, setHoveredZoneIds]);

  if (
    !selectionResults ||
    (selectionResults.zones.length === 0 && selectionResults.impacts.length === 0)
  ) {
    return null;
  }

  const intersectionsCount = selectionResults.zones.length;
  const constraintsCount = selectionResults.impacts.length;

  const renderMyMapTab = () => (
    <div className="space-y-4 text-sm">
      <div className="rounded-md border border-border bg-muted/40 p-3">
        <div className="text-xs font-medium text-muted-foreground">Selected area (in Km2)</div>
        <div className="mt-1 text-lg font-semibold text-foreground">{selectedAreaKm2.toFixed(2)} km²</div>
      </div>

      <div className="rounded-md border border-border bg-muted/40 p-3">
        <div className="text-xs font-medium text-muted-foreground">Lower altitude</div>
        <div className="mt-1 text-sm font-medium text-foreground">
          {drawingElevation.lowerBoundM ?? '-'} Meters AGL
        </div>
      </div>

      <div className="rounded-md border border-border bg-muted/40 p-3">
        <div className="text-xs font-medium text-muted-foreground">Upper altitude</div>
        <div className="mt-1 text-sm font-medium text-foreground">
          {drawingElevation.upperBoundM ?? '-'} Meters AGL
        </div>
      </div>
    </div>
  );

  const renderIntersectionsTab = () => {
    if (selectionResults.zones.length === 0) {
      return (
        <div className="text-sm italic text-muted-foreground">
          No intersecting zones for this selection.
        </div>
      );
    }

    return (
      <div className="space-y-2">
        {selectionResults.zones.map((zone) => {
          const checked = visibilityState ? visibilityState.has(zone.id) : true;

          return (
            <div
              key={zone.id}
              onMouseEnter={() => setHoveredZoneIds([zone.id])}
              onMouseLeave={() => setHoveredZoneIds([])}
              className="flex items-center gap-2 rounded border border-border bg-muted/30 px-3 py-2"
            >
              <input
                type="checkbox"
                checked={checked}
                onChange={(event) => setIntersectionZoneVisible(zone.id, event.target.checked)}
                className="h-4 w-4 rounded border-border"
              />

              <button
                type="button"
                onClick={() => selectZone(zone.id)}
                className="truncate text-left text-sm text-foreground hover:underline"
                title={zone.zoneName}
              >
                {zone.zoneName}
              </button>
            </div>
          );
        })}
      </div>
    );
  };

  const renderConstraintItem = (impact: Impact) => {
    const triggerZones = impact.triggerZones || [];

    return (
      <div
        key={impact.id}
        className="rounded border border-border bg-muted/30 px-3 py-2"
        onMouseEnter={() => {
          const zoneIds = triggerZones.map((zone) => zone.id);
          void ensureZoneGeometries(zoneIds);
          setHoveredZoneIds(zoneIds);
        }}
        onMouseLeave={() => setHoveredZoneIds([])}
      >
        <div className="text-sm font-medium text-foreground">{impact.name}</div>
        {impact.description && (
          <div className="mt-1 text-xs text-muted-foreground">{impact.description}</div>
        )}
      </div>
    );
  };

  const renderConstraintsTab = () => {
    if (selectionResults.impacts.length === 0) {
      return <div className="text-sm italic text-muted-foreground">No constraints for this selection.</div>;
    }

    return (
      <div className="space-y-4">
        {IMPACT_LEVEL_ORDER.map((level) => {
          const impacts = impactsByLevel[level] || [];
          if (impacts.length === 0) return null;

          return (
            <section key={level}>
              <div className="mb-2 flex items-center gap-2">
                <span className={`h-2.5 w-2.5 rounded-full ${IMPACT_LEVEL_META[level].dotClass}`} />
                <h3 className="text-sm font-semibold text-foreground">{IMPACT_LEVEL_META[level].label}</h3>
                <span className="text-xs text-muted-foreground">({impacts.length})</span>
              </div>
              <div className="space-y-2">{impacts.map((impact) => renderConstraintItem(impact))}</div>
            </section>
          );
        })}
      </div>
    );
  };

  return (
    <div
      className={`absolute z-10 flex flex-col overflow-hidden rounded-lg border border-border bg-card shadow-xl ${
        isMobile
          ? `left-2 right-2 ${isCollapsed ? 'bottom-2' : 'bottom-2 max-h-[62vh]'}`
          : `right-4 w-96 ${isCollapsed ? 'bottom-4' : 'bottom-4 top-20'}`
      }`}
    >
      <div className="flex items-center justify-between border-b border-border p-4">
        <h2 className="text-lg font-semibold text-foreground">Selection Results</h2>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => setIsCollapsed((prev) => !prev)}
            className="rounded p-1 transition hover:bg-accent"
            aria-label={isCollapsed ? 'Expand panel' : 'Collapse panel'}
            title={isCollapsed ? 'Expand' : 'Collapse'}
          >
            {isCollapsed ? (
              <ChevronDown className="h-5 w-5 text-muted-foreground" />
            ) : (
              <ChevronUp className="h-5 w-5 text-muted-foreground" />
            )}
          </button>
          <button
            onClick={clearSelection}
            className="rounded p-1 transition hover:bg-accent"
            aria-label="Close"
          >
            <X className="h-5 w-5 text-muted-foreground" />
          </button>
        </div>
      </div>

      {!isCollapsed && (
        <>
      <div className={`grid gap-1 border-b border-border bg-muted/30 p-2 ${isMobile ? 'grid-cols-1' : 'grid-cols-3'}`}>
        <button
          type="button"
          onClick={() => setActiveTab('my_map')}
          className={`rounded px-2 py-2 text-xs font-medium transition ${
            activeTab === 'my_map' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:bg-background/70'
          }`}
        >
          My Map
        </button>

        <button
          type="button"
          onClick={() => setActiveTab('intersections')}
          className={`rounded px-2 py-2 text-xs font-medium transition ${
            activeTab === 'intersections' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:bg-background/70'
          }`}
        >
          Intersections ({intersectionsCount})
        </button>

        <button
          type="button"
          onClick={() => setActiveTab('constraints')}
          className={`rounded px-2 py-2 text-xs font-medium transition ${
            activeTab === 'constraints' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:bg-background/70'
          }`}
        >
          Constraints ({constraintsCount})
        </button>
      </div>

      <div className={`flex-1 overflow-y-auto ${isMobile ? 'p-3' : 'p-4'}`}>
        {activeTab === 'my_map' && renderMyMapTab()}
        {activeTab === 'intersections' && renderIntersectionsTab()}
        {activeTab === 'constraints' && renderConstraintsTab()}
      </div>
        </>
      )}
    </div>
  );
};

export default UtmSelectionResultsPanel;
